import React, { useEffect, useMemo, useRef, useState } from "react";
import ProgramLabCheckoutFields from "./ProgramLabCheckoutFields";
import { checkLabAvailability, getLabCheckoutPolicies } from "../labs/services/labApi";
import {
  combinedLabHasServiceableMethod,
  programLabDetailKey,
  programLabDetail,
  programLabNeedsDrawSite,
  selectedCheckoutLabs,
  selectedProgramLabAvailability,
  selectedProgramLabMethod,
} from "./programLabCheckout";
import { visibleProgramLabCheckout } from "./programLabVisibility";
import { resolveStateFromZip } from "../labs/services/zipStateResolver";
import { parseRateLimitError } from "../../utils/rateLimit";
import { waitForPreviewSimulation } from "./previewSimulation";
import { previewLabAvailability } from "./previewDrawSites";

const sameDrawSite = (left, right) => Boolean(
  left && right && (
    (left.id && right.id && String(left.id) === String(right.id))
    || ((left.name || left.address)
      && left.name === right.name && left.address === right.address)
  )
);

export default function TreatmentLabCheckoutStep({
  question,
  value,
  onChange,
  questionnaire,
  allResponses,
  clientConfig,
  previewOnly = false,
  authority,
  continueTreatmentWithEligiblePrograms,
  customProgram = false,
  formError = "",
}) {
  const [availability, setAvailability] = useState({ status: "idle", message: "" });
  const [checkoutPolicies, setCheckoutPolicies] = useState(null);
  const [checkoutPoliciesLoading, setCheckoutPoliciesLoading] = useState(false);
  const availabilityRequestSequence = useRef(0);
  const automaticRefreshes = useRef(new Set());
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => () => {
    availabilityRequestSequence.current += 1;
  }, []);

  const mode = ["aoe", "consents"].includes(value?.lab_stage)
    ? value.lab_stage
    : "collection";

  const checkout = useMemo(() => {
    return visibleProgramLabCheckout(
      question,
      questionnaire,
      allResponses,
      { customProgram, previewOnly },
    );
  }, [allResponses, customProgram, previewOnly, question.program_labs, questionnaire]);

  const policyStorefrontSlug = selectedCheckoutLabs(checkout, value)
    .find((lab) => lab.storefront_slug)?.storefront_slug;

  // Load policies for the first storefront slug
  useEffect(() => {
    if (!policyStorefrontSlug) return;
    let active = true;
    setCheckoutPoliciesLoading(true);
    getLabCheckoutPolicies(policyStorefrontSlug)
      .then((res) => {
        if (active) setCheckoutPolicies(res?.policies || null);
      })
      .catch(() => {
        if (active) setCheckoutPolicies(null);
      })
      .finally(() => {
        if (active) setCheckoutPoliciesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [policyStorefrontSlug]);

  const checkAvailability = async (zipParam, { automatic = false } = {}) => {
    const requestSequence = ++availabilityRequestSequence.current;
    const address = value?.address || {};
    const zip = String(typeof zipParam === "string" ? zipParam : address.zip || "").trim();
    const returnToCollection = () => {
      if (automatic) onChange({ ...valueRef.current, lab_stage: "collection" });
    };
    let resolvedLocation = {};
    let state = "";

    if (zip.length < 5) {
      setAvailability({ status: "error", message: "Enter a valid 5-digit ZIP code." });
      returnToCollection();
      return;
    }

    setAvailability({ status: "loading", message: "Checking availability…" });

    try {
      const labs = checkout.treatments.flatMap((treatment) =>
        (treatment.labs || [])
          .map((lab) => ({
            ...lab,
            program_id: lab.program_id || treatment.program_id,
            program_name: lab.program_name || treatment.program_name,
          })),
      );

      if (previewOnly) {
        await waitForPreviewSimulation();
        if (requestSequence !== availabilityRequestSequence.current) return;
        const currentValue = valueRef.current || {};
        const details = { ...(currentValue.lab_details || {}) };
        let requiresCollectionReview = false;
        labs.forEach((lab) => {
          const detailKey = programLabDetailKey(lab);
          const previousDetail = programLabDetail(lab, details);
          const nextDetail = {
            ...previousDetail,
            collectionZip: zip,
            availability: {
              ...previewLabAvailability(lab),
              resolved_state: currentValue.address?.state || "",
              resolved_city: currentValue.address?.city || "",
              reason: "Preview availability",
            },
          };
          const sites = selectedProgramLabAvailability(lab, nextDetail)?.drawSites;
          const siteStillAvailable = automatic
            && Array.isArray(sites)
            && sites.some((site) => sameDrawSite(site, previousDetail.selectedSite));
          nextDetail.selectedSite = siteStillAvailable
            ? previousDetail.selectedSite : undefined;
          if (automatic && programLabNeedsDrawSite(lab, nextDetail)
            && !nextDetail.selectedSite) {
            requiresCollectionReview = true;
          }
          details[detailKey] = nextDetail;
        });
        onChange({
          ...currentValue,
          ...(automatic && requiresCollectionReview ? { lab_stage: "collection" } : {}),
          availability_zip: zip,
          availability_state: currentValue.address?.state || undefined,
          lab_details: details,
        });
        setAvailability({ status: "available", message: "All included labs are available at this location." });
        return;
      }

      try {
        resolvedLocation = await resolveStateFromZip(zip);
        state = resolvedLocation.state || "";
      } catch (error) {
        const rateLimitMsg = parseRateLimitError(error);
        if (rateLimitMsg) {
          setAvailability({ status: "error", message: rateLimitMsg });
          returnToCollection();
          return;
        }
        // The availability endpoint also resolves ZIPs server-side.
      }

      const checks = await Promise.all(labs.map(async (lab) => {
        if (!lab.storefront_slug) {
          throw new Error(`${lab.name || "Required lab"} has no active storefront configuration.`);
        }
        const result = await checkLabAvailability(
          lab.storefront_slug,
          zip,
          state,
          "25",
          {
            programId: lab.program_id,
            requirementId: lab.requirement_id || lab.assignment_id,
          },
        );
        const isAvailable = result.is_combined
          ? combinedLabHasServiceableMethod(result)
          : result.is_available;
        return { lab, result: { ...result, is_available: isAvailable } };
      }));
      if (requestSequence !== availabilityRequestSequence.current) return;
      const currentValue = valueRef.current || {};
      const currentAddress = currentValue.address || {};
      const includedLabKeys = new Set(
        selectedCheckoutLabs(checkout, currentValue).map((lab) => programLabDetailKey(lab)),
      );
      const failed = checks.find(({ lab, result }) => (
        includedLabKeys.has(programLabDetailKey(lab)) && !result.is_available
      ));
      const details = { ...(currentValue.lab_details || {}) };
      let requiresCollectionReview = Boolean(failed);
      checks.forEach(({ lab, result }) => {
        const detailKey = programLabDetailKey(lab);
        const decisionId = result.availability_decision?.decision_id || "";
        const previousDetail = programLabDetail(lab, details);
        const nextDetail = {
          ...previousDetail,
          // The server binds an availability decision to the ZIP that was
          // checked. Keep it with the decision instead of later substituting
          // a patient-address ZIP that may legitimately differ.
          collectionZip: zip,
          availability: result,
          availabilityDecisionId: decisionId,
        };
        const nextMethod = selectedProgramLabMethod(lab, nextDetail);
        const priorMethod = previousDetail.selectedMethod || lab.collection_method;
        const methodChanged = automatic && (
          !nextMethod
          || nextMethod.method !== priorMethod
          || (previousDetail.selectedAssignmentId
            && nextMethod.assignment_id !== previousDetail.selectedAssignmentId)
        );
        const sites = selectedProgramLabAvailability(lab, nextDetail)?.drawSites;
        const previousSite = previousDetail.selectedSite;
        const siteStillAvailable = automatic
          && Array.isArray(sites)
          && sites.some((site) => sameDrawSite(site, previousSite));
        nextDetail.selectedSite = siteStillAvailable ? previousSite : undefined;
        if (automatic && includedLabKeys.has(detailKey) && (
          methodChanged || programLabNeedsDrawSite(lab, nextDetail) && !nextDetail.selectedSite
        )) {
          requiresCollectionReview = true;
        }
        details[detailKey] = nextDetail;
      });
      const availabilityState = checks.find(({ result }) => result.resolved_state)
        ?.result.resolved_state || state;
      const availabilityCity = checks.find(({ result }) => result.resolved_city)
        ?.result.resolved_city || resolvedLocation.city || "";
      // Keep the checked service area separate from the patient address. The
      // address can then show a useful mismatch error instead of discarding
      // the successful availability result when a patient corrects it.
      onChange({
        ...currentValue,
        ...(automatic && requiresCollectionReview ? { lab_stage: "collection" } : {}),
        address: {
          ...currentAddress,
          zip: currentAddress.zip || zip,
          state: currentAddress.state || availabilityState,
          city: currentAddress.city || availabilityCity,
        },
        availability_zip: zip,
        availability_state: availabilityState,
        lab_details: details,
      });
      setAvailability(failed
        // A negative availability decision is expected domain data, not a
        // request failure. Its reason is rendered on the affected lab card.
        ? { status: "unavailable", message: "" }
        : { status: "available", message: "All included labs are available at this location." });
    } catch (error) {
      if (requestSequence !== availabilityRequestSequence.current) return;
      const rateLimitMsg = parseRateLimitError(error);
      setAvailability({ status: "error", message: rateLimitMsg || error.message || "Availability check failed. Try again." });
      returnToCollection();
    }
  };

  useEffect(() => {
    const selected = selectedCheckoutLabs(checkout, value);
    if (!selected.length) return;
    const missingAvailability = selected.some((lab) => (
      programLabDetail(lab, value?.lab_details)?.availability?.is_available !== true
    ));
    const collectionNeedsFullResult = mode === "collection" && selected.some((lab) => (
      programLabDetail(lab, value?.lab_details)?.availability?.source === "runtime_saved"
    ));
    if (!(mode === "consents" && missingAvailability) && !collectionNeedsFullResult) return;
    const zip = String(value?.availability_zip || value?.address?.zip || "").trim();
    const key = `${authority?.session_token || "preview"}:${authority?.session_revision || 0}:${zip}`;
    if (automaticRefreshes.current.has(key)) return;
    automaticRefreshes.current.add(key);
    checkAvailability(zip, { automatic: true });
  }, [authority?.session_revision, authority?.session_token, checkout, mode, value]);

  const handleServiceAreaZipChange = (zip) => {
    availabilityRequestSequence.current += 1;
    const address = value?.address || {};
    setAvailability({ status: "idle", message: "" });
    onChange({
      ...value,
      availability_zip: undefined,
      availability_state: undefined,
      address_verification: undefined,
      address: { ...address, street: "", apt: "", city: "", state: "", zip },
      lab_details: Object.fromEntries(
        Object.entries(value?.lab_details || {}).map(([assignmentId, detail]) => [
          assignmentId,
          {
            ...detail,
            availability: undefined,
            availabilityDecisionId: undefined,
            selectedSite: undefined,
          },
        ]),
      ),
    });
  };

  const brandName = (
    clientConfig?.questionnaire_config?.company_name
    || clientConfig?.branding_config?.company_name
    || clientConfig?.name
    || "Kin Meds"
  );

  return (
    <ProgramLabCheckoutFields
      checkout={checkout}
      value={value}
      onChange={onChange}
      availability={availability}
      onCheckAvailability={checkAvailability}
      onServiceAreaZipChange={handleServiceAreaZipChange}
      mode={mode}
      brandName={brandName}
      checkoutPolicies={checkoutPolicies}
      checkoutPoliciesLoading={checkoutPoliciesLoading}
      formError={formError}
      runtimeAvailability={question.runtime_availability || authority?.step?.availability}
      onContinueWithEligiblePrograms={continueTreatmentWithEligiblePrograms}
    />
  );
}
